import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Search, X, Package, Star, Plus, ChevronRight,
} from "lucide-react";
import { ASSET_CATEGORIES, findAsset } from "@/data/mapAssets";

const FAV_KEY_PREFIX    = "vtt_fav_assets_";
const RECENT_KEY_PREFIX = "vtt_recent_assets_";

// Map our existing categories onto the spec's category names
const CATEGORY_LABELS = {
  furniture: "Furniture",
  camp:      "Lighting",   // tents, lanterns, campfires
  nature:    "Nature",
  dungeon:   "Dungeon",
  town:      "Terrain",
  combat:    "Misc",
};

export const AssetBrowserPanel = ({
  open,
  onClose,
  campaignId,
  onPlaceAsset,    // (assetKey) => void  — quick spawn
  width: initialWidth = 320,
}) => {
  const [tab, setTab] = useState(ASSET_CATEGORIES[0].key);
  const [search, setSearch] = useState("");
  const [favOnly, setFavOnly] = useState(false);

  const favKey    = `${FAV_KEY_PREFIX}${campaignId}`;
  const recentKey = `${RECENT_KEY_PREFIX}${campaignId}`;
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem(favKey)) || []; } catch { return []; } });
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem(recentKey)) || []; } catch { return []; } });

  const toggleFav = (key) => {
    setFavs(prev => {
      const next = prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key];
      localStorage.setItem(favKey, JSON.stringify(next));
      return next;
    });
  };
  const noteRecent = (key) => {
    setRecent(prev => {
      const next = [key, ...prev.filter(x => x !== key)].slice(0, 30);
      localStorage.setItem(recentKey, JSON.stringify(next));
      return next;
    });
  };

  const [width, setWidth] = useState(() => parseInt(localStorage.getItem("vtt_asset_browser_w"), 10) || initialWidth);
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const move = (ev) => {
      const w = Math.max(260, Math.min(560, startW + (ev.clientX - startX)));
      setWidth(w);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      localStorage.setItem("vtt_asset_browser_w", String(width));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const list = useMemo(() => {
    let assets = [];
    if (tab === "recent") {
      assets = recent.map(k => findAsset(k)).filter(Boolean).map(a => ({ ...a, _cat: a.category }));
    } else if (tab === "favorites") {
      assets = favs.map(k => findAsset(k)).filter(Boolean).map(a => ({ ...a, _cat: a.category }));
    } else {
      const cat = ASSET_CATEGORIES.find(c => c.key === tab);
      assets = (cat?.assets || []).map(a => ({ ...a, _cat: tab }));
    }
    const q = search.trim().toLowerCase();
    if (q) assets = assets.filter(a => (a.name || "").toLowerCase().includes(q));
    if (favOnly) assets = assets.filter(a => favs.includes(a.key));
    return assets;
  }, [tab, search, favs, favOnly, recent]);

  const handleDragStart = (e, asset) => {
    e.dataTransfer.setData("application/x-natone-asset", JSON.stringify({ assetKey: asset.key }));
    e.dataTransfer.effectAllowed = "copy";
  };
  const quickSpawn = (asset) => {
    onPlaceAsset?.(asset.key);
    noteRecent(asset.key);
  };

  if (!open) return null;

  return (
    <div
      className="absolute top-12 left-14 z-30 bg-[#0f0f12]/95 backdrop-blur-md border border-[#333] rounded-r-lg shadow-2xl flex flex-col"
      style={{ width, height: "calc(100% - 4rem)" }}
      onWheel={(e) => e.stopPropagation()}
      data-vtt-sidebar="assets"
      data-testid="asset-browser-panel"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#333] bg-gradient-to-b from-[#1a1a22] to-[#101015] rounded-tr-lg">
        <h3 className="text-base font-bold text-white flex items-center gap-1.5"><Package className="w-4 h-4 text-primary" /> Asset Browser</h3>
        <button onClick={onClose} className="text-[#888] hover:text-white" data-testid="close-asset-browser"><X className="w-4 h-4" /></button>
      </div>

      {/* Category tabs — horizontally scrollable so labels never collide */}
      <div className="flex border-b border-[#333] bg-[#0a0a0d] overflow-x-auto scrollbar-thin" style={{ scrollbarWidth: "thin" }}>
        {ASSET_CATEGORIES.map(c => {
          const active = tab === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setTab(c.key)}
              className={`shrink-0 min-w-[88px] px-4 py-2.5 text-sm uppercase tracking-wider transition-colors whitespace-nowrap font-medium ${active ? "bg-primary/20 text-primary border-b-2 border-primary" : "text-[#aaa] hover:text-white"}`}
              data-testid={`ab-tab-${c.key}`}
            >
              {c.label}
            </button>
          );
        })}
        <button
          onClick={() => setTab("favorites")}
          className={`shrink-0 min-w-[80px] px-4 py-2.5 text-sm uppercase tracking-wider transition-colors whitespace-nowrap font-medium ${tab === "favorites" ? "bg-primary/20 text-primary border-b-2 border-primary" : "text-[#aaa] hover:text-white"}`}
          data-testid="ab-tab-favorites"
        >★ Favs</button>
        <button
          onClick={() => setTab("recent")}
          className={`shrink-0 min-w-[80px] px-4 py-2.5 text-sm uppercase tracking-wider transition-colors whitespace-nowrap font-medium ${tab === "recent" ? "bg-primary/20 text-primary border-b-2 border-primary" : "text-[#aaa] hover:text-white"}`}
          data-testid="ab-tab-recent"
        >Recent</button>
      </div>

      <div className="p-2 border-b border-[#333]">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search assets…"
            className="h-8 text-sm pl-7 bg-[#1a1a1a] border-[#333] text-white"
            data-testid="ab-search"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-2" data-testid="ab-grid">
        {list.length === 0 ? (
          <p className="p-4 text-sm text-[#666] italic text-center">Nothing here.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {list.map(a => {
              const isFav = favs.includes(a.key);
              return (
                <div
                  key={a.key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, a)}
                  onDoubleClick={() => quickSpawn(a)}
                  className="relative bg-[#181820] border border-[#2a2a2a] hover:border-primary/50 rounded-sm p-1.5 cursor-grab active:cursor-grabbing group transition-colors"
                  data-testid={`ab-item-${a.key}`}
                  title={a.name}
                >
                  <div className="aspect-square bg-[#0a0a0d] rounded-sm flex items-center justify-center overflow-hidden">
                    <img src={a.url} alt={a.name} className="w-full h-full object-contain" draggable={false} />
                  </div>
                  <p className="text-xs text-center text-[#ccc] truncate mt-1">{a.name}</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFav(a.key); }}
                    className={`absolute top-1 right-1 p-0.5 rounded-sm ${isFav ? "text-yellow-400 opacity-100" : "text-[#666] opacity-0 group-hover:opacity-100"} transition-opacity`}
                    data-testid={`ab-fav-${a.key}`}
                  >
                    <Star className={`w-3.5 h-3.5 ${isFav ? "fill-yellow-400" : ""}`} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); quickSpawn(a); }}
                    className="absolute bottom-1 right-1 p-0.5 rounded-sm bg-primary/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Quick spawn"
                    data-testid={`ab-quickspawn-${a.key}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-3 py-1.5 border-t border-[#333] bg-[#0a0a0d] text-xs text-[#666]">
        Drag to map · double-click = quick spawn · ★ favorite
      </div>

      <div onPointerDown={startResize} className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize bg-transparent hover:bg-primary/30" data-testid="ab-resize" />
    </div>
  );
};

export default AssetBrowserPanel;
